import { http } from '@/api/http';
import type { Subscription, AccessDetails } from '@/api/auth';

export interface ApiTerminal {
  id: number;
  name: string;
  terminal_provider?: string;
  location_id: number;
  status: string | null;
  // Extra per-terminal settings — sometimes a JSON string, sometimes already an object. CLI/POS
  // merges this on top of the base terminal record (device_type, terminal_reader,
  // supports_signature, etc.) — see resolveTerminalConfig() in src/api/cart.ts.
  options?: string | Record<string, unknown> | null;
}

export interface ApiLocation {
  id: number;
  name: string;
  status: number;
  stores_terminals: ApiTerminal[];
}

interface ApiEnvelope<T> {
  status: string;
  result: T;
}

export async function getLocations(): Promise<ApiLocation[]> {
  const data = await http.get<ApiEnvelope<{ data: ApiLocation[] }>>('/locations');
  if (data.status !== 'OK') throw new Error('Failed to load locations');
  return data.result.data ?? [];
}

export function cacheLocations(locations: ApiLocation[]): void {
  localStorage.setItem('locations', JSON.stringify(locations));
}

// Matches CLI's sendStoreData() (cart.service.ts) — tells the backend which
// location/terminal is now active.
export async function chooseLocation(locationId: number, terminalId: number | null): Promise<void> {
  await http.post('/locations/choose', { location_id: locationId, terminal_id: terminalId });
}

// Matches CLI's getUserDataByLocation() (admin.service.ts) — called right after
// chooseLocation(). Confirmed via a live captured response that this returns a
// normal, directly-usable v2 JWT (not CLI's legacy encrypted blob), with `location`
// baked into the token's own payload — which is exactly why the old token kept
// scoping requests to the old location no matter what location_id individual
// requests sent.
export interface LocationTokenResult {
  token: string;
  refresh_token: string;
  user_id: number;
  user_type_id: number;
  name: string;
  email: string;
  subscription: Subscription;
  company_id: number;
  store_id: number;
  store_uid: string;
  store_name: string;
  store_slug: string;
  setup: unknown;
  payment_plan: unknown;
  location_id: string;
  terminal_id: number | string;
  timezone: string;
  free_plan_popup: boolean;
  free_plan_mode: string;
  logo: string;
  access_details: AccessDetails;
}

export async function getLocationScopedToken(locationId: number): Promise<LocationTokenResult> {
  const data = await http.get<ApiEnvelope<{ data: LocationTokenResult }>>(`/v2/users/token?location=${locationId}`);
  if (data.status !== 'OK') throw new Error('Failed to refresh location-scoped token');
  return data.result.data;
}

export function resolveLocationName(locationId: number | null | undefined): string {
  if (locationId == null) return '—';
  try {
    const list: ApiLocation[] = JSON.parse(localStorage.getItem('locations') ?? '[]');
    return list.find(l => l.id === locationId)?.name ?? '—';
  } catch {
    return '—';
  }
}

